import { createFileRoute } from "@tanstack/react-router";
import { Settings } from "lucide-react";
import { useCallback, useEffect } from "react";
import { z } from "zod";

import { PageShell } from "@/components/layouts/shells/page-shell";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { settingsTabs } from "@/constants/settings-tabs.constants";
import { useUser } from "@/features/auth/hooks/use-user";
import { m } from "@/paraglide/messages.js";

const TOUR_SET_TAB = "preview-tour-set-settings-tab";

export const Route = createFileRoute("/(private)/settings/")({
  component: RouteComponent,
  loader: () => {
    return {
      crumb: m.settings_page_title(),
    };
  },
  validateSearch: z.object({
    tab: z.string().optional(),
  }),
});

function RouteComponent() {
  const { hasPermission } = useUser();
  const navigate = Route.useNavigate();
  const { tab } = Route.useSearch();

  const visibleTabs = settingsTabs.filter(
    (entry) => !entry.permission || hasPermission(entry.permission.action, entry.permission.subject),
  );
  const activeTab = visibleTabs.find((entry) => entry.id === tab)?.id ?? visibleTabs[0]?.id;

  const setTab = useCallback(
    (value: string) => {
      // An explicit `to` + plain search object instead of a functional updater:
      // without `to`, the pack typecheck resolves navigate against AnyRouter and
      // rejects any search value. `tab` is the route's whole search schema.
      navigate({ to: "/settings", search: { tab: value }, replace: true });
    },
    [navigate],
  );

  useEffect(() => {
    const onSetTab = (event: Event): void => {
      const value = (event as CustomEvent<{ tab?: string }>).detail?.tab;
      if (value) setTab(value);
    };
    window.addEventListener(TOUR_SET_TAB, onSetTab);
    return () => window.removeEventListener(TOUR_SET_TAB, onSetTab);
  }, [setTab]);

  return (
    <PageShell icon={Settings} title={m.settings_page_title()} description={m.settings_page_description()}>
      <Tabs value={activeTab} onValueChange={setTab} className="space-y-6">
        <TabsList>
          {visibleTabs.map((entry) => (
            <TabsTrigger key={entry.id} value={entry.id} className="gap-1.5" data-tour={`settings-${entry.id}`}>
              <entry.icon className="h-3.5 w-3.5" />
              {entry.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {visibleTabs.map((entry) => (
          <TabsContent key={entry.id} value={entry.id} data-tour={`settings-${entry.id}-content`}>
            <entry.component />
          </TabsContent>
        ))}
      </Tabs>
    </PageShell>
  );
}
